import type { Locale } from "../i18n";

/*
  Source of truth for the public Privacy Policy and Terms of Service pages,
  keyed by the shared `Locale` contract (en / zh-Hant / zh-Hans / ja). These
  are crawled by Google as part of OAuth consent-screen verification, so they
  are SSR'd (English first) and the same locales as the rest of the app are
  offered via a client switch.

  Japanese legal copy has not been through professional legal review yet, so
  `ja` falls back to the English source until that review lands.

  Keep the locales structurally identical: same section ids, same order.
*/

export interface LegalSection {
  id: string;
  heading: string;
  /** Plain-text paragraphs. */
  paras?: string[];
  /** Optional bullet list rendered after the paragraphs. */
  bullets?: string[];
}

export interface LegalDoc {
  /** Document title shown as the H1. */
  title: string;
  /** "Last updated" line, already localized. */
  updated: string;
  /** Lead paragraphs above the first numbered section. */
  intro: string[];
  sections: LegalSection[];
}

/** Single place to change the effective date across every locale. */
const EFFECTIVE_DATE = { en: "June 25, 2026", "zh-Hans": "2026 年 6 月 25 日", "zh-Hant": "2026 年 6 月 25 日" } as const;

/** Contact addresses surfaced in both documents. */
export const LEGAL_CONTACT = {
  privacy: "info@salesko.ai",
  legal: "info@salesko.ai",
  entity: "Salesko"
} as const;

/* ============================ PRIVACY POLICY ============================ */

const privacyEn: LegalDoc = {
    title: "Privacy Policy",
    updated: `Last updated: ${EFFECTIVE_DATE.en}`,
    intro: [
      `This Privacy Policy explains how ${LEGAL_CONTACT.entity} ("Salesko", "we", "us") collects, uses, and protects your information when you use the Salesko web workspace, agent integrations, and related services (together, the "Service").`,
      "By using the Service you agree to the practices described here. If you do not agree, please do not use the Service."
    ],
    sections: [
      {
        id: "collect",
        heading: "1. Information we collect",
        paras: ["We collect only what we need to run the Service for you:"],
        bullets: [
          "Account information — when you sign in with Google, we receive your name, email address, profile picture, and Google account identifier.",
          "Content you provide — the meeting notes, contacts, companies, events, and other text you add so the agent can build your relationship graph.",
          "Usage and technical data — log data such as IP address, browser type, pages viewed, and timestamps, used to keep the Service secure and reliable."
        ]
      },
      {
        id: "google",
        heading: "2. Google user data and Limited Use",
        paras: [
          "When you sign in with Google, Salesko accesses your basic profile (name, email, profile picture) solely to create and secure your account.",
          "Salesko's use and transfer of information received from Google APIs adheres to the Google API Services User Data Policy, including the Limited Use requirements. We do not use Google user data for advertising, and we do not sell it.",
          "You can revoke Salesko's access at any time from your Google Account permissions page."
        ]
      },
      {
        id: "use",
        heading: "3. How we use your information",
        bullets: [
          "To provide, maintain, and improve the Service and your relationship graph.",
          "To authenticate you and keep your account secure.",
          "To process your notes with AI models so entities and connections can be extracted.",
          "To respond to support requests and send essential service notices.",
          "To detect, prevent, and address fraud, abuse, and security issues."
        ]
      },
      {
        id: "ai",
        heading: "4. AI processing",
        paras: [
          "The Service uses AI models to turn your notes into a structured graph. The content you submit may be sent to model providers acting as our processors solely to perform this extraction.",
          "We do not permit these providers to use your content to train their general models, and we instruct them to handle it only on our behalf."
        ]
      },
      {
        id: "share",
        heading: "5. How we share information",
        paras: ["We do not sell your personal information. We share it only with:"],
        bullets: [
          "Service providers (subprocessors) such as cloud hosting (Cloudflare) and AI model providers, who process data only on our instructions.",
          "Authorities when required by law, or to protect the rights, safety, and security of users and the Service.",
          "A successor entity in connection with a merger, acquisition, or asset sale, subject to this Policy."
        ]
      },
      {
        id: "retention",
        heading: "6. Data retention",
        paras: [
          "We keep your information for as long as your account is active or as needed to provide the Service. You can delete your content or close your account, after which we delete or anonymize associated data within a reasonable period, except where retention is required by law."
        ]
      },
      {
        id: "security",
        heading: "7. Security",
        paras: [
          "We use industry-standard safeguards including encryption in transit and access controls. No method of transmission or storage is completely secure, so we cannot guarantee absolute security."
        ]
      },
      {
        id: "rights",
        heading: "8. Your rights and choices",
        paras: [
          `Depending on your location, you may have the right to access, correct, export, or delete your personal data, and to object to or restrict certain processing. To exercise these rights, contact us at ${LEGAL_CONTACT.privacy}.`
        ]
      },
      {
        id: "transfers",
        heading: "9. International transfers",
        paras: [
          "The Service runs on global edge infrastructure, so your information may be processed in countries other than your own. Where required, we use appropriate safeguards for such transfers."
        ]
      },
      {
        id: "children",
        heading: "10. Children",
        paras: [
          "The Service is not directed to children under 16, and we do not knowingly collect their personal information."
        ]
      },
      {
        id: "changes",
        heading: "11. Changes to this Policy",
        paras: [
          "We may update this Policy from time to time. Material changes will be reflected by updating the date above and, where appropriate, through a notice in the Service."
        ]
      },
      {
        id: "contact",
        heading: "12. Contact us",
        paras: [
          `If you have questions about this Privacy Policy, contact us at ${LEGAL_CONTACT.privacy}.`
        ]
      }
    ]
};

const privacy: Record<Locale, LegalDoc> = {
  en: privacyEn,

  "zh-Hans": {
    title: "隐私政策",
    updated: `最后更新：${EFFECTIVE_DATE["zh-Hans"]}`,
    intro: [
      `本隐私政策说明 ${LEGAL_CONTACT.entity}（“Salesko”“我们”）在你使用 Salesko 网页工作台、agent 接入及相关服务（统称“本服务”）时，如何收集、使用和保护你的信息。`,
      "使用本服务即表示你同意这里描述的做法。如不同意，请勿使用本服务。"
    ],
    sections: [
      {
        id: "collect",
        heading: "1. 我们收集的信息",
        paras: ["我们只收集运行本服务所必需的信息："],
        bullets: [
          "账户信息——当你用 Google 登录时，我们会收到你的姓名、电子邮箱、头像和 Google 账户标识。",
          "你提供的内容——你录入的会面笔记、联系人、公司、事件等文本，供 agent 构建你的关系图谱。",
          "使用与技术数据——IP 地址、浏览器类型、访问页面、时间戳等日志数据，用于保障服务的安全与稳定。"
        ]
      },
      {
        id: "google",
        heading: "2. Google 用户数据与有限使用",
        paras: [
          "当你用 Google 登录时，Salesko 仅为创建和保护你的账户而访问你的基本资料（姓名、邮箱、头像）。",
          "Salesko 对从 Google API 获取信息的使用与传输，遵守 Google API Services User Data Policy，包括其 Limited Use（有限使用）要求。我们不会将 Google 用户数据用于广告，也不会出售。",
          "你可以随时在 Google 账户的权限页面撤销 Salesko 的访问授权。"
        ]
      },
      {
        id: "use",
        heading: "3. 我们如何使用你的信息",
        bullets: [
          "提供、维护和改进本服务及你的关系图谱。",
          "对你进行身份验证，保障账户安全。",
          "用 AI 模型处理你的笔记，抽取其中的实体与关系。",
          "回应支持请求，发送必要的服务通知。",
          "检测、预防和处理欺诈、滥用与安全问题。"
        ]
      },
      {
        id: "ai",
        heading: "4. AI 处理",
        paras: [
          "本服务使用 AI 模型把你的笔记转成结构化图谱。你提交的内容可能会发送给作为我们处理方的模型提供商，仅用于完成这项抽取。",
          "我们不允许这些提供商将你的内容用于训练其通用模型，并要求他们仅代表我们处理这些内容。"
        ]
      },
      {
        id: "share",
        heading: "5. 我们如何共享信息",
        paras: ["我们不会出售你的个人信息。我们仅在以下情况共享："],
        bullets: [
          "服务提供商（子处理方），如云托管（Cloudflare）和 AI 模型提供商，他们仅按我们的指示处理数据。",
          "在法律要求时，或为保护用户与本服务的权利、安全时，向相关机构披露。",
          "在合并、收购或资产出售中向继受方披露，并继续受本政策约束。"
        ]
      },
      {
        id: "retention",
        heading: "6. 数据保留",
        paras: [
          "只要你的账户处于活跃状态、或为提供本服务所必需，我们会保留你的信息。你可以删除你的内容或注销账户，之后我们会在合理期限内删除或匿名化相关数据，法律另有要求的除外。"
        ]
      },
      {
        id: "security",
        heading: "7. 安全",
        paras: [
          "我们采用行业标准的保护措施，包括传输加密和访问控制。但没有任何传输或存储方式是绝对安全的，因此我们无法保证绝对安全。"
        ]
      },
      {
        id: "rights",
        heading: "8. 你的权利与选择",
        paras: [
          `根据你所在地区的法律，你可能有权访问、更正、导出或删除你的个人数据，并反对或限制某些处理。如需行使这些权利，请联系 ${LEGAL_CONTACT.privacy}。`
        ]
      },
      {
        id: "transfers",
        heading: "9. 跨境传输",
        paras: [
          "本服务运行在全球边缘基础设施上，你的信息可能在你所在国家／地区以外被处理。在法律要求时，我们会为此类传输采取适当的保护措施。"
        ]
      },
      {
        id: "children",
        heading: "10. 未成年人",
        paras: [
          "本服务不面向 16 岁以下儿童，我们也不会在知情的情况下收集他们的个人信息。"
        ]
      },
      {
        id: "changes",
        heading: "11. 本政策的变更",
        paras: [
          "我们可能会不时更新本政策。重大变更会通过更新上方日期、并在适当时通过服务内通知体现。"
        ]
      },
      {
        id: "contact",
        heading: "12. 联系我们",
        paras: [
          `如对本隐私政策有任何疑问，请联系 ${LEGAL_CONTACT.privacy}。`
        ]
      }
    ]
  },

  "zh-Hant": {
    title: "隱私政策",
    updated: `最後更新：${EFFECTIVE_DATE["zh-Hant"]}`,
    intro: [
      `本隱私政策說明 ${LEGAL_CONTACT.entity}（「Salesko」「我們」）在你使用 Salesko 網頁工作台、agent 接入及相關服務（統稱「本服務」）時，如何蒐集、使用和保護你的資訊。`,
      "使用本服務即表示你同意這裡描述的做法。如不同意，請勿使用本服務。"
    ],
    sections: [
      {
        id: "collect",
        heading: "1. 我們蒐集的資訊",
        paras: ["我們只蒐集運行本服務所必需的資訊："],
        bullets: [
          "帳戶資訊——當你用 Google 登入時，我們會收到你的姓名、電子郵件、頭像和 Google 帳戶識別碼。",
          "你提供的內容——你輸入的會面筆記、聯絡人、公司、事件等文字，供 agent 建構你的關係圖譜。",
          "使用與技術資料——IP 位址、瀏覽器類型、瀏覽頁面、時間戳等記錄資料，用於保障服務的安全與穩定。"
        ]
      },
      {
        id: "google",
        heading: "2. Google 使用者資料與有限使用",
        paras: [
          "當你用 Google 登入時，Salesko 僅為建立和保護你的帳戶而存取你的基本資料（姓名、郵件、頭像）。",
          "Salesko 對從 Google API 取得資訊的使用與傳輸，遵守 Google API Services User Data Policy，包括其 Limited Use（有限使用）要求。我們不會將 Google 使用者資料用於廣告，也不會出售。",
          "你可以隨時在 Google 帳戶的權限頁面撤銷 Salesko 的存取授權。"
        ]
      },
      {
        id: "use",
        heading: "3. 我們如何使用你的資訊",
        bullets: [
          "提供、維護和改進本服務及你的關係圖譜。",
          "對你進行身分驗證，保障帳戶安全。",
          "用 AI 模型處理你的筆記，抽取其中的實體與關係。",
          "回應支援請求，發送必要的服務通知。",
          "偵測、預防和處理詐騙、濫用與安全問題。"
        ]
      },
      {
        id: "ai",
        heading: "4. AI 處理",
        paras: [
          "本服務使用 AI 模型把你的筆記轉成結構化圖譜。你提交的內容可能會傳送給作為我們處理方的模型供應商，僅用於完成這項抽取。",
          "我們不允許這些供應商將你的內容用於訓練其通用模型，並要求他們僅代表我們處理這些內容。"
        ]
      },
      {
        id: "share",
        heading: "5. 我們如何分享資訊",
        paras: ["我們不會出售你的個人資訊。我們僅在以下情況分享："],
        bullets: [
          "服務供應商（子處理方），如雲端託管（Cloudflare）和 AI 模型供應商，他們僅依我們的指示處理資料。",
          "在法律要求時，或為保護使用者與本服務的權利、安全時，向相關機關揭露。",
          "在合併、收購或資產出售中向繼受方揭露，並繼續受本政策約束。"
        ]
      },
      {
        id: "retention",
        heading: "6. 資料保留",
        paras: [
          "只要你的帳戶處於活躍狀態、或為提供本服務所必需，我們會保留你的資訊。你可以刪除你的內容或註銷帳戶，之後我們會在合理期限內刪除或匿名化相關資料，法律另有要求者除外。"
        ]
      },
      {
        id: "security",
        heading: "7. 安全",
        paras: [
          "我們採用業界標準的保護措施，包括傳輸加密和存取控制。但沒有任何傳輸或儲存方式是絕對安全的，因此我們無法保證絕對安全。"
        ]
      },
      {
        id: "rights",
        heading: "8. 你的權利與選擇",
        paras: [
          `根據你所在地區的法律，你可能有權存取、更正、匯出或刪除你的個人資料，並反對或限制某些處理。如需行使這些權利，請聯絡 ${LEGAL_CONTACT.privacy}。`
        ]
      },
      {
        id: "transfers",
        heading: "9. 跨境傳輸",
        paras: [
          "本服務運行在全球邊緣基礎設施上，你的資訊可能在你所在國家／地區以外被處理。在法律要求時，我們會為此類傳輸採取適當的保護措施。"
        ]
      },
      {
        id: "children",
        heading: "10. 未成年人",
        paras: [
          "本服務不面向 16 歲以下兒童，我們也不會在知情的情況下蒐集他們的個人資訊。"
        ]
      },
      {
        id: "changes",
        heading: "11. 本政策的變更",
        paras: [
          "我們可能會不時更新本政策。重大變更會透過更新上方日期、並在適當時透過服務內通知體現。"
        ]
      },
      {
        id: "contact",
        heading: "12. 聯絡我們",
        paras: [
          `如對本隱私政策有任何疑問，請聯絡 ${LEGAL_CONTACT.privacy}。`
        ]
      }
    ]
  },

  // Japanese legal copy pending professional review — fall back to English source.
  ja: privacyEn
};

/* ============================ TERMS OF SERVICE ============================ */

const termsEn: LegalDoc = {
    title: "Terms of Service",
    updated: `Last updated: ${EFFECTIVE_DATE.en}`,
    intro: [
      `These Terms of Service ("Terms") govern your access to and use of the Salesko web workspace, agent integrations, and related services (the "Service") provided by ${LEGAL_CONTACT.entity} ("Salesko", "we", "us").`,
      "By accessing or using the Service, you agree to be bound by these Terms. If you do not agree, do not use the Service."
    ],
    sections: [
      {
        id: "eligibility",
        heading: "1. Eligibility and accounts",
        paras: [
          "You must be at least 16 years old and able to form a binding contract to use the Service. You are responsible for keeping your account credentials secure and for all activity under your account."
        ]
      },
      {
        id: "use",
        heading: "2. Acceptable use",
        paras: ["You agree not to:"],
        bullets: [
          "Use the Service in violation of any law or third-party right, including privacy and data-protection rights of the people in your notes.",
          "Upload malware, attempt to breach security, or disrupt or overload the Service.",
          "Reverse engineer, resell, or build a competing service from the Service except as permitted by law.",
          "Use the Service to send spam or to harass, abuse, or harm others."
        ]
      },
      {
        id: "content",
        heading: "3. Your content",
        paras: [
          "You retain all rights to the notes, contacts, and other content you submit (\"Your Content\"). You grant Salesko a limited license to host, process, and display Your Content solely to operate and improve the Service for you.",
          "You are responsible for ensuring you have the necessary rights and lawful basis to submit Your Content, including any personal data about third parties."
        ]
      },
      {
        id: "ai",
        heading: "4. AI features",
        paras: [
          "The Service uses AI models to extract entities and relationships from Your Content. AI output may be inaccurate or incomplete and should not be relied on as professional advice. You are responsible for reviewing results before acting on them."
        ]
      },
      {
        id: "beta",
        heading: "5. Beta and availability",
        paras: [
          "The Service is currently offered in beta and may be free of charge. It is provided on an evolving basis: features may change, be limited, or be discontinued, and we do not guarantee uninterrupted availability."
        ]
      },
      {
        id: "third-party",
        heading: "6. Third-party services",
        paras: [
          "The Service integrates with third parties such as Google sign-in, MCP clients, and AI model providers. Your use of those services is governed by their own terms, and we are not responsible for them."
        ]
      },
      {
        id: "ip",
        heading: "7. Intellectual property",
        paras: [
          "The Service, including its software, design, and trademarks, is owned by Salesko and protected by law. Except for the rights expressly granted to you, we reserve all rights in the Service."
        ]
      },
      {
        id: "disclaimer",
        heading: "8. Disclaimers",
        paras: [
          'The Service is provided "as is" and "as available" without warranties of any kind, whether express or implied, including merchantability, fitness for a particular purpose, and non-infringement, to the fullest extent permitted by law.'
        ]
      },
      {
        id: "liability",
        heading: "9. Limitation of liability",
        paras: [
          "To the fullest extent permitted by law, Salesko will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of data, profits, or goodwill, arising from your use of the Service."
        ]
      },
      {
        id: "termination",
        heading: "10. Termination",
        paras: [
          "You may stop using the Service at any time. We may suspend or terminate your access if you breach these Terms or to protect the Service. On termination, your right to use the Service ends, and we may delete Your Content as described in our Privacy Policy."
        ]
      },
      {
        id: "changes",
        heading: "11. Changes to these Terms",
        paras: [
          "We may update these Terms from time to time. Material changes will be reflected by updating the date above and, where appropriate, through a notice in the Service. Continued use after changes take effect means you accept the updated Terms."
        ]
      },
      {
        id: "contact",
        heading: "12. Contact us",
        paras: [
          `If you have questions about these Terms, contact us at ${LEGAL_CONTACT.legal}.`
        ]
      }
    ]
};

const terms: Record<Locale, LegalDoc> = {
  en: termsEn,

  "zh-Hans": {
    title: "服务条款",
    updated: `最后更新：${EFFECTIVE_DATE["zh-Hans"]}`,
    intro: [
      `本服务条款（“条款”）约束你对 ${LEGAL_CONTACT.entity}（“Salesko”“我们”）提供的 Salesko 网页工作台、agent 接入及相关服务（“本服务”）的访问与使用。`,
      "访问或使用本服务即表示你同意受本条款约束。如不同意，请勿使用本服务。"
    ],
    sections: [
      {
        id: "eligibility",
        heading: "1. 资格与账户",
        paras: [
          "你须年满 16 周岁且具备订立有约束力合同的能力，方可使用本服务。你应妥善保管账户凭据，并对账户下的所有活动负责。"
        ]
      },
      {
        id: "use",
        heading: "2. 可接受使用",
        paras: ["你同意不会："],
        bullets: [
          "以违反任何法律或第三方权利的方式使用本服务，包括你笔记中所涉人员的隐私与数据保护权利。",
          "上传恶意软件、试图破坏安全，或扰乱、超载本服务。",
          "对本服务进行逆向工程、转售，或据其搭建竞争性服务，法律允许的除外。",
          "利用本服务发送垃圾信息，或骚扰、辱骂、伤害他人。"
        ]
      },
      {
        id: "content",
        heading: "3. 你的内容",
        paras: [
          "你保留对所提交的笔记、联系人及其他内容（“你的内容”）的全部权利。你授予 Salesko 一项有限许可，仅为面向你运营和改进本服务而托管、处理和展示你的内容。",
          "你应确保自己拥有提交“你的内容”所需的权利与合法依据，包括其中涉及第三方的任何个人数据。"
        ]
      },
      {
        id: "ai",
        heading: "4. AI 功能",
        paras: [
          "本服务使用 AI 模型从“你的内容”中抽取实体与关系。AI 输出可能不准确或不完整，不应作为专业意见依赖。你应在据其行动前自行核查结果。"
        ]
      },
      {
        id: "beta",
        heading: "5. 内测与可用性",
        paras: [
          "本服务目前处于内测阶段，可能免费提供。本服务持续演进：功能可能变更、受限或停止，我们不保证服务不中断。"
        ]
      },
      {
        id: "third-party",
        heading: "6. 第三方服务",
        paras: [
          "本服务与第三方集成，如 Google 登录、MCP 客户端和 AI 模型提供商。你对这些服务的使用受其各自条款约束，我们对此不承担责任。"
        ]
      },
      {
        id: "ip",
        heading: "7. 知识产权",
        paras: [
          "本服务（含其软件、设计与商标）归 Salesko 所有并受法律保护。除明确授予你的权利外，我们保留对本服务的一切权利。"
        ]
      },
      {
        id: "disclaimer",
        heading: "8. 免责声明",
        paras: [
          "在法律允许的最大范围内，本服务按“现状”和“现有”提供，不作任何明示或默示的保证，包括适销性、特定用途适用性和不侵权。"
        ]
      },
      {
        id: "liability",
        heading: "9. 责任限制",
        paras: [
          "在法律允许的最大范围内，对于因你使用本服务而产生的任何间接、附带、特殊、后果性或惩罚性损害，或任何数据、利润或商誉损失，Salesko 概不负责。"
        ]
      },
      {
        id: "termination",
        heading: "10. 终止",
        paras: [
          "你可随时停止使用本服务。若你违反本条款，或为保护本服务，我们可暂停或终止你的访问。终止后，你使用本服务的权利即告结束，我们可按隐私政策所述删除“你的内容”。"
        ]
      },
      {
        id: "changes",
        heading: "11. 本条款的变更",
        paras: [
          "我们可能会不时更新本条款。重大变更会通过更新上方日期、并在适当时通过服务内通知体现。变更生效后继续使用，即表示你接受更新后的条款。"
        ]
      },
      {
        id: "contact",
        heading: "12. 联系我们",
        paras: [
          `如对本条款有任何疑问，请联系 ${LEGAL_CONTACT.legal}。`
        ]
      }
    ]
  },

  "zh-Hant": {
    title: "服務條款",
    updated: `最後更新：${EFFECTIVE_DATE["zh-Hant"]}`,
    intro: [
      `本服務條款（「條款」）規範你對 ${LEGAL_CONTACT.entity}（「Salesko」「我們」）提供的 Salesko 網頁工作台、agent 接入及相關服務（「本服務」）的存取與使用。`,
      "存取或使用本服務即表示你同意受本條款約束。如不同意，請勿使用本服務。"
    ],
    sections: [
      {
        id: "eligibility",
        heading: "1. 資格與帳戶",
        paras: [
          "你須年滿 16 歲且具備訂立有約束力合約的能力，方可使用本服務。你應妥善保管帳戶憑證，並對帳戶下的所有活動負責。"
        ]
      },
      {
        id: "use",
        heading: "2. 可接受使用",
        paras: ["你同意不會："],
        bullets: [
          "以違反任何法律或第三方權利的方式使用本服務，包括你筆記中所涉人員的隱私與資料保護權利。",
          "上傳惡意軟體、試圖破壞安全，或擾亂、超載本服務。",
          "對本服務進行逆向工程、轉售，或據其搭建競爭性服務，法律允許者除外。",
          "利用本服務發送垃圾訊息，或騷擾、辱罵、傷害他人。"
        ]
      },
      {
        id: "content",
        heading: "3. 你的內容",
        paras: [
          "你保留對所提交的筆記、聯絡人及其他內容（「你的內容」）的全部權利。你授予 Salesko 一項有限授權，僅為面向你營運和改進本服務而託管、處理和顯示你的內容。",
          "你應確保自己擁有提交「你的內容」所需的權利與合法依據，包括其中涉及第三方的任何個人資料。"
        ]
      },
      {
        id: "ai",
        heading: "4. AI 功能",
        paras: [
          "本服務使用 AI 模型從「你的內容」中抽取實體與關係。AI 輸出可能不準確或不完整，不應作為專業意見依賴。你應在據其行動前自行核查結果。"
        ]
      },
      {
        id: "beta",
        heading: "5. 內測與可用性",
        paras: [
          "本服務目前處於內測階段，可能免費提供。本服務持續演進：功能可能變更、受限或停止，我們不保證服務不中斷。"
        ]
      },
      {
        id: "third-party",
        heading: "6. 第三方服務",
        paras: [
          "本服務與第三方整合，如 Google 登入、MCP 客戶端和 AI 模型供應商。你對這些服務的使用受其各自條款約束，我們對此不承擔責任。"
        ]
      },
      {
        id: "ip",
        heading: "7. 智慧財產權",
        paras: [
          "本服務（含其軟體、設計與商標）歸 Salesko 所有並受法律保護。除明確授予你的權利外，我們保留對本服務的一切權利。"
        ]
      },
      {
        id: "disclaimer",
        heading: "8. 免責聲明",
        paras: [
          "在法律允許的最大範圍內，本服務按「現狀」和「現有」提供，不作任何明示或默示的保證，包括適銷性、特定用途適用性和不侵權。"
        ]
      },
      {
        id: "liability",
        heading: "9. 責任限制",
        paras: [
          "在法律允許的最大範圍內，對於因你使用本服務而產生的任何間接、附帶、特殊、後果性或懲罰性損害，或任何資料、利潤或商譽損失，Salesko 概不負責。"
        ]
      },
      {
        id: "termination",
        heading: "10. 終止",
        paras: [
          "你可隨時停止使用本服務。若你違反本條款，或為保護本服務，我們可暫停或終止你的存取。終止後，你使用本服務的權利即告結束，我們可依隱私政策所述刪除「你的內容」。"
        ]
      },
      {
        id: "changes",
        heading: "11. 本條款的變更",
        paras: [
          "我們可能會不時更新本條款。重大變更會透過更新上方日期、並在適當時透過服務內通知體現。變更生效後繼續使用，即表示你接受更新後的條款。"
        ]
      },
      {
        id: "contact",
        heading: "12. 聯絡我們",
        paras: [
          `如對本條款有任何疑問，請聯絡 ${LEGAL_CONTACT.legal}。`
        ]
      }
    ]
  },

  // Japanese legal copy pending professional review — fall back to English source.
  ja: termsEn
};

export const LEGAL_DOCS = { privacy, terms } as const;
export type LegalDocKey = keyof typeof LEGAL_DOCS;

// Japanese legal copy is intentionally English-only until professional review.
// Render that document text with the English language tag instead of labelling
// English legal prose as Japanese.
export const LEGAL_CONTENT_LOCALE: Record<Locale, Locale> = {
  en: "en",
  "zh-Hans": "zh-Hans",
  "zh-Hant": "zh-Hant",
  ja: "en"
};

/** UI strings for the legal page chrome (nav, switcher, footer), per locale. */
export const LEGAL_UI: Record<Locale, { back: string; tagline: string; home: string; privacy: string; terms: string; copyright: string }> = {
  en: {
    back: "Back to Salesko",
    tagline: "Contacts Intelligence",
    home: "Home",
    privacy: "Privacy",
    terms: "Terms",
    copyright: "© 2026 Salesko. All rights reserved."
  },
  "zh-Hans": {
    back: "返回 Salesko",
    tagline: "Contacts Intelligence",
    home: "首页",
    privacy: "隐私",
    terms: "条款",
    copyright: "© 2026 Salesko. 保留所有权利。"
  },
  "zh-Hant": {
    back: "返回 Salesko",
    tagline: "Contacts Intelligence",
    home: "首頁",
    privacy: "隱私",
    terms: "條款",
    copyright: "© 2026 Salesko. 保留所有權利。"
  },
  // Japanese UI copy pending professional review — fall back to English source.
  ja: {
    back: "Back to Salesko",
    tagline: "Contacts Intelligence",
    home: "Home",
    privacy: "Privacy",
    terms: "Terms",
    copyright: "© 2026 Salesko. All rights reserved."
  }
};
